import { isHttpError, type IHttpClient, type Session, type UserProfile } from "@crialume/sdk";
import { decodeJwt, encodeUnsignedJwt, getJwtExpiry, readStringList, type JwtClaims } from "./jwt";

export interface LoginResult {
  accessToken: string;
  /** Dados de exibição (nome, avatar). NUNCA a fonte de permissões. */
  user?: Partial<UserProfile>;
  /** Nome da empresa, para exibição. */
  tenantName?: string;
}

/** Perfil de exibição carregado do backend quando o token não traz nome/e-mail. */
export interface ProfileResult {
  user: Partial<UserProfile>;
  tenantName?: string;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

/**
 * Tudo que é específico do backend/produto fica no adapter — o AuthService
 * é genérico. No CadFrn os papéis (`grpcslcadfor`...) e o formato da resposta
 * de login estavam fixos dentro do core.
 */
export interface AuthAdapter {
  login(credentials: Record<string, unknown>, http: IHttpClient): Promise<LoginResult>;
  /**
   * Deriva a sessão SEMPRE do token. O usuário salvo serve só para exibição:
   * adulterar o storage não escala privilégio.
   */
  resolveSession(token: string, storedUser: UserProfile | null): Session | null;
  logout?(http: IHttpClient): void | Promise<void>;
  /**
   * Renova a sessão sem credenciais (ex.: refresh token em cookie HttpOnly).
   * `null` = não há sessão para renovar. Erro de rede deve propagar.
   */
  refresh?(http: IHttpClient): Promise<LoginResult | null>;
  /** Carrega nome/e-mail/empresa quando o token não carrega dado pessoal. */
  loadProfile?(http: IHttpClient, accessToken: string): Promise<ProfileResult>;
  /** Troca a senha do usuário corrente e devolve a sessão nova. */
  changePassword?(input: ChangePasswordInput, http: IHttpClient): Promise<LoginResult>;
}

export interface JwtAuthAdapterOptions {
  loginPath: string;
  /** Renovação silenciosa (refresh token fora do alcance do JS, em cookie). */
  refreshPath?: string;
  /** Encerra a sessão no servidor (revoga o refresh token). */
  logoutPath?: string;
  /** Perfil do usuário corrente (`GET`). */
  profilePath?: string;
  changePasswordPath?: string;
  /** Extrai o token da resposta do login/refresh. */
  extractToken?: (response: unknown) => string | undefined;
  extractUser?: (response: unknown, claims: JwtClaims) => Partial<UserProfile>;
  /** Extrai o perfil da resposta de `profilePath`. Padrão: `{ name, email, tenantName }`. */
  extractProfile?: (response: unknown) => ProfileResult;
  /** Claim com os papéis. Padrão: "role". Comparados em minúsculas. */
  rolesClaim?: string;
  /** Mapa papel → permissões. Papéis desconhecidos são ignorados. */
  rolePermissions: Record<string, readonly string[]>;
  /** Permissões vindas direto de uma claim (opcional, somadas às dos papéis). */
  permissionsClaim?: string;
  /** Claim com o id da empresa (ex.: "tenant_id"). */
  tenantClaim?: string;
  /** Claim presente quando a senha é provisória (ex.: "pwd_change"). */
  passwordChangeClaim?: string;
}

const defaultExtractToken = (response: unknown) => {
  const r = response as Record<string, Record<string, unknown> | undefined> | undefined;
  const token = r?.["accessToken"] ?? r?.["access_token"] ?? r?.["data"]?.["access_token"];
  return typeof token === "string" ? token : undefined;
};

const defaultExtractProfile = (response: unknown): ProfileResult => {
  const r = (response ?? {}) as Record<string, unknown>;
  const text = (key: string) => (typeof r[key] === "string" ? (r[key] as string) : undefined);
  const name = text("name");
  const email = text("email");
  const tenantName = text("tenantName");
  return {
    user: { ...(name ? { name } : {}), ...(email ? { email } : {}) },
    ...(tenantName ? { tenantName } : {}),
  };
};

/** 400/401/403 na renovação = não há sessão válida (não é falha de rede). */
const isSessionRejection = (error: unknown) =>
  isHttpError(error) && error.status >= 400 && error.status < 500;

export function createJwtAuthAdapter(options: JwtAuthAdapterOptions): AuthAdapter {
  const rolesClaim = options.rolesClaim ?? "role";
  const extractToken = options.extractToken ?? defaultExtractToken;
  const extractProfile = options.extractProfile ?? defaultExtractProfile;

  const toResult = (response: unknown): LoginResult => {
    const accessToken = extractToken(response);
    if (!accessToken) throw new Error("Resposta de autenticação sem token de acesso.");
    const claims = decodeJwt(accessToken) ?? {};
    const user = options.extractUser?.(response, claims);
    return { accessToken, ...(user ? { user } : {}) };
  };

  const adapter: AuthAdapter = {
    async login(credentials, http) {
      return toResult(
        await http.post<unknown>(options.loginPath, credentials, { anonymous: true }),
      );
    },

    resolveSession(token, storedUser) {
      const claims = decodeJwt(token);
      if (!claims) return null;
      const expiresAt = getJwtExpiry(claims);
      if (!expiresAt) return null;

      const known = new Set(Object.keys(options.rolePermissions));
      const roles = readStringList(claims[rolesClaim])
        .map((role) => role.toLowerCase())
        .filter((role) => known.has(role));

      const permissions = new Set<string>(
        options.permissionsClaim ? readStringList(claims[options.permissionsClaim]) : [],
      );
      for (const role of roles) {
        for (const permission of options.rolePermissions[role] ?? []) permissions.add(permission);
      }

      const tenantId = options.tenantClaim ? claims[options.tenantClaim] : undefined;
      const pendingFlag = options.passwordChangeClaim
        ? claims[options.passwordChangeClaim]
        : undefined;

      return {
        user: {
          id: claims.sub ?? storedUser?.id ?? "unknown",
          name: storedUser?.name ?? claims.name ?? "Usuário",
          email: storedUser?.email ?? claims.email,
          avatarUrl: storedUser?.avatarUrl,
        },
        roles,
        permissions: [...permissions],
        expiresAt,
        ...(pendingFlag === true || pendingFlag === "true" ? { mustChangePassword: true } : {}),
        ...(typeof tenantId === "string" && tenantId ? { tenant: { id: tenantId } } : {}),
      };
    },
  };

  if (options.logoutPath) {
    const path = options.logoutPath;
    adapter.logout = async (http) => {
      await http.post(path, undefined, { anonymous: true });
    };
  }

  if (options.refreshPath) {
    const path = options.refreshPath;
    adapter.refresh = async (http) => {
      try {
        const response = await http.post<unknown>(path, undefined, { anonymous: true });
        // 204: o navegador não tem cookie de renovação, então não há sessão.
        if (response === undefined) return null;
        return toResult(response);
      } catch (error) {
        if (isSessionRejection(error)) return null;
        throw error;
      }
    };
  }

  if (options.profilePath) {
    const path = options.profilePath;
    adapter.loadProfile = async (http, accessToken) =>
      extractProfile(
        // O token ainda não está no AuthService: vai explícito no cabeçalho.
        await http.get<unknown>(path, {
          anonymous: true,
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      );
  }

  if (options.changePasswordPath) {
    const path = options.changePasswordPath;
    adapter.changePassword = async (input, http) => toResult(await http.post<unknown>(path, input));
  }

  return adapter;
}

/**
 * Adapter de DESENVOLVIMENTO: aceita qualquer usuário/senha e gera um token
 * local com os papéis informados. Ativado por `auth.mode = "mock"` no config.json.
 */
export function createMockAuthAdapter(options: {
  roles: string[];
  rolePermissions: Record<string, readonly string[]>;
  ttlMs?: number;
}): AuthAdapter {
  const jwt = createJwtAuthAdapter({ loginPath: "", rolePermissions: options.rolePermissions });
  return {
    async login(credentials) {
      const username = String(credentials["email"] ?? credentials["username"] ?? "dev");
      const expiresAt = Date.now() + (options.ttlMs ?? 8 * 60 * 60 * 1000);
      const accessToken = encodeUnsignedJwt({
        sub: username,
        name: username,
        role: options.roles,
        exp: Math.floor(expiresAt / 1000),
      });
      return { accessToken, user: { name: username } };
    },
    resolveSession: jwt.resolveSession,
  };
}
